import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { computeResizeSync } from '@/lib/boundaries/bounding-box-boundary'
import { localVecToCanvas } from '@/lib/transforms/geometry'
import { MIN_DIMENSION } from '../constants/panel-limits'
import type { ResizeHandle } from '../types/resize-handle'
import { clampPanelResizeToPhotoFill } from './photo-fill-resize'
import {
  computeLocalPanelResizeDeltas,
  orderedPanelResizeCandidates,
  scorePanelResizeDirection,
} from './resize-direction'

/**
 * Requested width/height (pt) for a panel W/H field commit, before any resize-direction/rotation
 * math or max-size clamping — i.e. what the user is asking for. When `linked`, the field not
 * being typed into scales proportionally from the shape's current aspect ratio.
 */
export function computeTargetDimensionsForPanelField(params: {
  w0: number
  h0: number
  field: 'width' | 'height'
  targetPoints: number
  linked: boolean
}): { width: number; height: number } {
  const { w0, h0, field, targetPoints, linked } = params
  const width = linked
    ? field === 'width'
      ? Math.max(MIN_DIMENSION, targetPoints)
      : Math.max(MIN_DIMENSION, (w0 / h0) * targetPoints)
    : field === 'width'
      ? Math.max(MIN_DIMENSION, targetPoints)
      : w0
  const height = linked
    ? field === 'height'
      ? Math.max(MIN_DIMENSION, targetPoints)
      : Math.max(MIN_DIMENSION, (h0 / w0) * targetPoints)
    : field === 'height'
      ? Math.max(MIN_DIMENSION, targetPoints)
      : h0

  return { width, height }
}

export function computeShapeResizeFromPanelDimension(params: {
  shape: ShapeData
  field: 'width' | 'height'
  targetPoints: number
  linked: boolean
}): { x: number; y: number; width: number; height: number } | null {
  const { shape, field, targetPoints, linked } = params
  const w0 = shape.width ?? 0
  const h0 = shape.height ?? 0

  if (!Number.isFinite(w0) || !Number.isFinite(h0) || w0 <= 0 || h0 <= 0) {
    return null
  }

  const rotation = shape.rotation ?? 0
  const { width: targetWidth, height: targetHeight } = computeTargetDimensionsForPanelField({
    w0,
    h0,
    field,
    targetPoints,
    linked,
  })
  const widthDelta = targetWidth - w0
  const heightDelta = targetHeight - h0
  const candidateDirections = orderedPanelResizeCandidates(field, linked, rotation)

  let best:
    | {
        score: number
        geometry: { x: number; y: number; width: number; height: number }
        direction: ResizeHandle
      }
    | undefined

  for (const direction of candidateDirections) {
    const { dx: localDx, dy: localDy } = computeLocalPanelResizeDeltas(
      direction,
      widthDelta,
      heightDelta
    )
    const nextLocal = linked
      ? computeResizeSync(
          {
            direction,
            initial: { x: -w0 / 2, y: -h0 / 2, width: w0, height: h0 },
            dx: localDx,
            dy: localDy,
            minDimension: MIN_DIMENSION,
          },
          { constrainAspect: true, axis: field === 'width' ? 'width' : 'height' }
        )
      : computeResizeSync({
          direction,
          initial: { x: -w0 / 2, y: -h0 / 2, width: w0, height: h0 },
          dx: localDx,
          dy: localDy,
          minDimension: MIN_DIMENSION,
        })

    const initialCenter = {
      x: shape.x + w0 / 2,
      y: shape.y + h0 / 2,
    }
    const centerLocal = {
      x: nextLocal.x + nextLocal.width / 2,
      y: nextLocal.y + nextLocal.height / 2,
    }
    const centerOffset = localVecToCanvas(centerLocal, rotation)
    const nextCenter = {
      x: initialCenter.x + centerOffset.x,
      y: initialCenter.y + centerOffset.y,
    }
    const geometry = {
      x: nextCenter.x - nextLocal.width / 2,
      y: nextCenter.y - nextLocal.height / 2,
      width: nextLocal.width,
      height: nextLocal.height,
    }
    const score = scorePanelResizeDirection({ shape, candidate: geometry })

    if (!best || score < best.score) {
      best = { score, geometry, direction }
    }
  }

  if (!best) return null

  if (shape.photoFill) {
    return clampPanelResizeToPhotoFill({
      shape,
      photoFill: shape.photoFill,
      direction: best.direction,
      next: best.geometry,
      linked,
    })
  }

  return best.geometry
}
